import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { DocumentHtml } from '../components/DocumentHtml';
import { fetchContent, type DocPage } from '../lib/content';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { StandardsAccessGate } from '../components/StandardsAccessGate';
import { StandardDownload } from '../components/StandardDownload';

export function DocsPage() {
  const { isAuthenticated, sessionToken } = useAuth();
  const params = useParams();
  const location = useLocation();
  const slug = params.standard ? `standard-${params.standard.toLowerCase()}` : params.slug ?? 'index';
  const [html, setHtml] = useState<string | null>(null);
  const [page, setPage] = useState<DocPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setHtml(null);
    setPage(null);
    setError(null);
    void (async () => {
      try {
        const index = await fetchContent<DocPage[]>('index.json', controller.signal);
        const page = index.find((item) => item.slug === slug);
        if (!page) throw new Error('This documentation page does not exist.');
        if (controller.signal.aborted) return;
        setPage(page);
        document.title = `${page.title} · AstdZ`;
        if (page.requiresAccount && !isAuthenticated) return;
        let text: string;
        if (page.requiresAccount) {
          const document = await apiFetch<{ html: string }>(`/v1/content/documents/${page.slug}`, { signal: controller.signal });
          text = document.html;
        } else {
          const response = await fetch(`/docs-content/${page.slug}.html`, { signal: controller.signal });
          if (!response.ok) throw new Error('The documentation could not be loaded. Please try again.');
          text = await response.text();
        }
        if (!text.startsWith('<!-- astdz-document -->')) throw new Error('The documentation could not be loaded. Please try again.');
        if (controller.signal.aborted) return;
        document.title = `${page.title} · AstdZ`;
        setHtml(text);
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : 'Unable to load documentation.');
      }
    })();
    return () => controller.abort();
  }, [slug, retry, isAuthenticated, sessionToken]);
  useEffect(() => {
    if (html === null) return;
    const id = location.hash.slice(1);
    let decodedId = id;
    try { decodedId = decodeURIComponent(id); } catch { /* retain literal hash */ }
    if (decodedId) document.getElementById(decodedId)?.scrollIntoView();
    else window.scrollTo({ top: 0 });
  }, [html, location.hash]);
  if (page?.requiresAccount && !isAuthenticated) return <StandardsAccessGate title={page.title} />;
  if (error) return <div className="docs-content" role="alert">
    <h1>Documentation unavailable</h1><p>{error}</p>
    <button className="btn" onClick={() => setRetry((value) => value + 1)}>Try again</button>
    <p><Link to="/docs/standards">Browse all standards</Link></p>
  </div>;
  if (html === null) return <p className="text-muted" role="status">Loading documentation…</p>;
  return <>{page?.filename && <StandardDownload key={slug} slug={slug} filename={page.filename} />}<DocumentHtml html={html} /></>;
}
